import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import {
  fieldLabel,
  formatFieldValue,
  isMarkupOnlyChange,
  type ValueReference,
} from "./historyFields";
import type {
  FieldDiff,
  HistoryCollection,
  RecordLabels,
} from "../../../core/history/entities/HistoryVersion";

const valueBase = "whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm leading-relaxed";
const valueScroll = "max-h-56 overflow-y-auto";

interface ValueCellProps {
  field: string;
  value: unknown;
  tone: "before" | "after" | "neutral";
  labels: RecordLabels;
}

/// Une valeur de champ. Les teintes restent basses : sur une liste de champs, des blocs saturés
/// se liraient comme des erreurs plutôt que comme un avant/après.
function ValueCell({ field, value, tone, labels }: ValueCellProps) {
  const { t } = useTranslation();
  const formatted = formatFieldValue(
    field,
    value,
    t("history.emptyValue"),
    labels,
  );

  // Le texte est teinté depuis la même famille que son fond : du gris sur un fond coloré
  // paraît délavé, et la teinte porte ici du sens (retiré / ajouté).
  const tones = {
    before:
      "bg-red-50/70 text-red-950 dark:bg-red-950/30 dark:text-red-50",
    after:
      "bg-emerald-50/70 text-emerald-950 dark:bg-emerald-950/30 dark:text-emerald-50",
    neutral:
      "bg-gray-50 text-gray-800 dark:bg-gray-800/60 dark:text-gray-100",
  };

  return (
    <div className={`${valueBase} ${valueScroll} ${tones[tone]}`}>
      {formatted.references ? (
        <ReferenceList references={formatted.references} />
      ) : formatted.isEmpty ? (
        <span className="italic text-gray-400 dark:text-gray-500">
          {formatted.text}
        </span>
      ) : (
        formatted.text
      )}
    </div>
  );
}

/// Enregistrements désignés par un champ relation : leur nom d'abord, l'id ensuite.
///
/// L'id reste affiché — c'est lui qui figure dans le snapshot, et le seul moyen de retrouver
/// l'élément quand deux d'entre eux portent le même titre — mais en second plan : un rédacteur
/// lit un titre, pas une chaîne de 15 caractères.
function ReferenceList({ references }: { references: ValueReference[] }) {
  const { t } = useTranslation();

  return (
    <ul className="space-y-1.5">
      {references.map((reference) => (
        <li
          key={reference.id}
          className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
        >
          {reference.label ? (
            <span>{reference.label}</span>
          ) : (
            // Ni enregistrement vivant, ni trace dans l'historique : il ne reste que l'id.
            <span className="italic opacity-70">
              {t("history.unknownRecord")}
            </span>
          )}
          <span className="font-mono text-xs opacity-60">{reference.id}</span>
          {reference.isDeleted && !!reference.label && (
            <span className="text-xs opacity-60">
              {t("history.deletedRecord")}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

interface HistoryDiffProps {
  collection: HistoryCollection;
  diffs: FieldDiff[];
  /// Libellés des enregistrements désignés par un id dans l'un ou l'autre des états comparés.
  labels: RecordLabels;
  /// `change` : avant/après. `single` : un seul état (création, suppression).
  layout: "change" | "single";
  /// Colonne à afficher en mode `single`.
  singleSide?: "before" | "after";
  beforeLabel: string;
  afterLabel: string;
  emptyMessage: string;
}

/// Détail d'un événement d'historique : seuls les champs qui diffèrent.
export function HistoryDiff({
  collection,
  diffs,
  labels,
  layout,
  singleSide = "before",
  beforeLabel,
  afterLabel,
  emptyMessage,
}: HistoryDiffProps) {
  const { t } = useTranslation();

  if (!diffs.length) {
    return (
      <p className="rounded-lg bg-gray-50 px-4 py-6 text-sm text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
        {emptyMessage}
      </p>
    );
  }

  if (layout === "single") {
    return (
      <dl className="space-y-3">
        {diffs.map((diff) => (
          <div key={diff.field}>
            <dt className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {fieldLabel(collection, diff.field, t)}
            </dt>
            <dd>
              <ValueCell
                field={diff.field}
                value={singleSide === "before" ? diff.before : diff.after}
                tone="neutral"
                labels={labels}
              />
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <div className="space-y-5">
      <div className="hidden items-center gap-3 text-xs font-medium text-gray-400 dark:text-gray-500 md:grid md:grid-cols-[1fr_auto_1fr]">
        <span className="truncate">{beforeLabel}</span>
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="truncate">{afterLabel}</span>
      </div>

      <dl className="space-y-5">
        {diffs.map((diff) => {
          const markupOnly = isMarkupOnlyChange(
            diff.field,
            diff.before,
            diff.after,
          );

          return (
            <div key={diff.field}>
              <dt className="mb-1.5 flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {fieldLabel(collection, diff.field, t)}
                </span>
                {markupOnly && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {t("history.markupOnly")}
                  </span>
                )}
              </dt>
              <dd className="grid gap-3 md:grid-cols-[1fr_auto_1fr] md:items-start md:gap-3">
                <ValueCell
                  field={diff.field}
                  value={diff.before}
                  tone="before"
                  labels={labels}
                />
                <ArrowRight
                  className="hidden h-4 w-4 shrink-0 self-center text-gray-300 dark:text-gray-600 md:block"
                  aria-hidden="true"
                />
                <ValueCell
                  field={diff.field}
                  value={diff.after}
                  tone="after"
                  labels={labels}
                />
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
